import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, Button } from "@datanomik/commons-ui";
import {
  ShieldOutlined,
  VisibilityOffOutlined,
  BuildOutlined,
  RocketLaunchOutlined,
  ArrowForward,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import { DatanomikIsoWhite } from "@/views/common/Logo";

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const features = [
  {
    icon: VisibilityOffOutlined,
    titleKey: "demo_feature_data_title",
    descKey: "demo_feature_data_desc",
  },
  {
    icon: BuildOutlined,
    titleKey: "demo_feature_limited_title",
    descKey: "demo_feature_limited_desc",
  },
  {
    icon: ShieldOutlined,
    titleKey: "demo_feature_safe_title",
    descKey: "demo_feature_safe_desc",
  },
  {
    icon: RocketLaunchOutlined,
    titleKey: "demo_feature_quick_title",
    descKey: "demo_feature_quick_desc",
  },
];

interface DemoIntroPageProps {
  onContinue: () => void;
}

export const DemoIntroPage: React.FC<DemoIntroPageProps> = ({ onContinue }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #1a1f3c 0%, #1e2a5e 30%, #2845D2 60%, #6366f1 100%)",
        backgroundSize: "400% 400%",
        animation: `${shimmer} 15s ease infinite`,
        p: 3,
      }}
    >
      <Stack
        alignItems="center"
        sx={{
          maxWidth: 520,
          width: "100%",
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        {/* Logo */}
        <DatanomikIsoWhite
          style={{ width: 48, height: 48, marginBottom: 24 }}
        />

        {/* Badge */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 2,
            py: 0.5,
            mb: 3,
            borderRadius: "20px",
            bgcolor: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: "#4ade80",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {t("demo_header_label")}
          </Typography>
        </Box>

        {/* Title */}
        <Typography
          sx={{
            color: "#fff",
            fontSize: { xs: 28, sm: 34 },
            fontWeight: 700,
            textAlign: "center",
            lineHeight: 1.2,
            mb: 1.5,
          }}
        >
          {t("demo_header_title")}
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            color: "rgba(255,255,255,0.75)",
            fontSize: 14,
            textAlign: "center",
            lineHeight: 1.6,
            mb: 4,
            maxWidth: 420,
          }}
        >
          {t("demo_header_subtitle")}
        </Typography>

        {/* Features card */}
        <Box
          sx={{
            width: "100%",
            bgcolor: "rgba(255,255,255,0.07)",
            backdropFilter: "blur(16px)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.12)",
            p: { xs: 2.5, sm: 3.5 },
            mb: 4,
          }}
        >
          <Stack spacing={2.5}>
            {features.map(({ icon: Icon, titleKey, descKey }, i) => (
              <Stack
                key={i}
                direction="row"
                spacing={2}
                alignItems="flex-start"
                sx={{
                  animation: `${fadeInUp} 0.5s ease-out`,
                  animationDelay: `${0.15 + i * 0.08}s`,
                  animationFillMode: "backwards",
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.1)",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 19, color: "rgba(255,255,255,0.85)" }} />
                </Box>
                <Box>
                  <Typography
                    sx={{ fontSize: 13.5, fontWeight: 600, color: "#fff" }}
                  >
                    {t(titleKey)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      color: "rgba(255,255,255,0.6)",
                      mt: 0.25,
                      lineHeight: 1.5,
                    }}
                  >
                    {t(descKey)}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>

        {/* CTA */}
        <Button
          variant="contained"
          size="large"
          onClick={onContinue}
          endIcon={<ArrowForward />}
          sx={{
            bgcolor: "#fff",
            color: "#2845D2",
            fontWeight: 700,
            fontSize: 15,
            px: 5,
            py: 1.5,
            borderRadius: "12px",
            textTransform: "none",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.9)",
            },
          }}
        >
          {t("demo_cta")}
        </Button>
      </Stack>
    </Box>
  );
};
